/**
 * 岡山県の背景ツアー「岡山めぐり」コンテンツ。
 *
 * 軸は一日の時間。早朝の備中松山城から、夜の倉敷美観地区へ。
 *
 * 後楽園について。
 *  「日本三名園」は使わない。岡山県立施設の公式サイトには断定形で載っているが、
 *  香川の栗林公園で同じ表現を避けている。県をまたいで判断を揃える方を選んだ。
 *  書けるのは1952年の特別名勝指定という文化財としての事実。
 *
 * 備中松山城について。
 *  雲海に浮かぶ姿は兵庫の竹田城跡ですでに使っている。
 *  ここでは「天守が残る唯一の山城」「岩盤の上にそのまま積まれた石垣」を主題にする。
 *  雲海は9月から4月ごろの明け方に限られるという限定情報としてのみ触れる。
 *
 * 鷲羽山について。
 *  瀬戸大橋は香川（坂出市）ですでに使っている。
 *  ここでは橋を画面に入れず、重なり合う多島美と夕日を主題にする。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 「日本のエーゲ海」（牛窓）は公式でも伝聞形。使わない。
 *  - 岡山城の天守を「現存天守」と書かない。1966年の再建。現存天守は備中松山城のほう。
 *  - 大原美術館の所蔵作品名は列挙しない（著作権）。
 *  - 桃太郎は民話・温羅伝説の範囲まで。特定の出版物やキャラクターデザインに触れない。
 *  - 営業時間・点灯時刻・入園料は年で変わる。書かない。
 *  - 旧閑谷学校の創建年は出典で食い違う（寛文10年と寛文11年）。
 *    それぞれの出典に沿って別々の豆知識に分けてある。
 */

import type { StageTour } from '../types';

export const okayamaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'bitchu-matsuyama-jo',
      name: '備中松山城',
      area: '高梁市・晩秋の早朝',
      image: '/assets/scenery/okayama/scenes/01_bitchu_matsuyama_jo.webp',
      hotspots: [
        {
          id: 'bitchu-yuiitsu',
          x: 0.46,
          y: 0.38,
          title: '天守が残る唯一の山城とされる',
          body: '岡山県観光連盟は備中松山城を、天守が残る日本で唯一の山城と紹介しています。江戸時代から残る現存天守12城のひとつです。山上に天守があるため、登城口のふいご峠から天守までは徒歩でおよそ20分かかります。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10823.html',
          mapQuery: '備中松山城',
        },
        {
          id: 'bitchu-iwakabe',
          x: 0.74,
          y: 0.52,
          title: '高さ10メートルを超える岩壁',
          body: '山城の周辺には高さ10メートル以上の巨大な岩壁がそびえ、難攻不落の名城と呼ばれた面影が残っています。城へは8合目のふいご峠から、天守まで歩いて登ります。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10823.html',
          mapQuery: '備中松山城 大手門跡',
        },
        {
          id: 'bitchu-unkai',
          x: 0.22,
          y: 0.48,
          title: '雲海は9月から4月の明け方',
          body: '備中松山城は、雲海に浮かぶ天空の山城として全国的に人気の撮影スポットになっています。雲海が発生するのは9月から4月ごろの明け方で、その時期を狙って訪れるよう案内されています。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10823.html',
          mapQuery: '高梁市 備中松山城',
        },
      ],
    },
    {
      id: 'korakuen',
      name: '岡山後楽園',
      area: '岡山市北区・朝',
      image: '/assets/scenery/okayama/scenes/02_korakuen.webp',
      hotspots: [
        {
          id: 'korakuen-meisho',
          x: 0.46,
          y: 0.44,
          title: '特別名勝に指定された庭園',
          body: '岡山藩2代目藩主の池田綱政が、自らの憩いの場として築いた大庭園です。1952年には歴史的文化遺産として特別名勝に指定されました。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10001.html',
          mapQuery: '岡山後楽園',
        },
        {
          id: 'korakuen-sawanoike',
          x: 0.74,
          y: 0.36,
          title: '三つの島が浮かぶ沢の池',
          body: '園内中央にある沢の池には、左から島茶屋のある中の島、釣殿のある御野島、白砂青松が美しい砂利島の三つの島が浮かびます。',
          source: 'https://okayama-korakuen.jp/midokoro/midokoro.html',
          mapQuery: '後楽園 沢の池',
        },
        {
          id: 'korakuen-yuishinzan',
          x: 0.22,
          y: 0.5,
          title: '庭を立体にした唯心山',
          body: '唯心山は池田綱政の子の継政の時に築かれた築山で、それまで平面的だった庭園が立体的な景観へと変わりました。斜面には石組に合わせてツツジやサツキが植えられ、季節には紅白の花で彩られます。',
          source: 'https://okayama-korakuen.jp/midokoro/midokoro.html',
          mapQuery: '後楽園 唯心山',
        },
      ],
    },
    {
      id: 'hiruzen-kogen',
      name: '蒜山高原',
      area: '真庭市・初夏の午前',
      image: '/assets/scenery/okayama/scenes/03_hiruzen_kogen.webp',
      hotspots: [
        {
          id: 'hiruzen-sanza',
          x: 0.46,
          y: 0.4,
          title: '蒜山三座を背にした放牧地',
          body: '蒜山高原は岡山県真庭市にある高原で、蒜山三座の雄大な姿と、放牧中のジャージー牛がのんびりと草を食む様子が眺められます。西日本屈指の高原リゾートとして紹介されています。',
          source: 'https://www.okayama-kanko.jp/spot/detail_11439.html',
          mapQuery: '蒜山高原',
        },
        {
          id: 'hiruzen-kokuritsu',
          x: 0.74,
          y: 0.5,
          title: '大山隠岐国立公園のなか',
          body: '蒜山は環境省の大山隠岐国立公園に含まれます。同公園の公式サイトでは蒜山高原から見た蒜山三座のライブ映像が公開されており、公園自体は「神話がつなぐ山と島」というテーマで紹介されています。',
          source: 'https://www.env.go.jp/park/daisen/',
          mapQuery: '蒜山三座',
        },
        {
          id: 'hiruzen-cycling',
          x: 0.22,
          y: 0.48,
          title: '初夏は自転車がよく似合う',
          body: '春から初夏にかけての高原特有の気候はサイクリングに適しているとされています。ジャージー牛乳を使ったスイーツや乳製品など、高原ならではの食べものもあわせて楽しめます。',
          source: 'https://www.okayama-kanko.jp/spot/detail_11439.html',
          mapQuery: '蒜山高原 サイクリング',
        },
      ],
    },
    {
      id: 'kinojo',
      name: '鬼ノ城',
      area: '総社市・昼',
      image: '/assets/scenery/okayama/scenes/04_kinojo.webp',
      hotspots: [
        {
          id: 'kinojo-nazo',
          x: 0.46,
          y: 0.42,
          title: '歴史書に載らない古代山城',
          body: '鬼ノ城は、大和朝廷が国の防衛のために築いたとされる古代山城です。ただし歴史書には一切記されておらず、その歴史は解明されずに謎のまま残されています。日本100名城のひとつに数えられています。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10755.html',
          mapQuery: '鬼ノ城',
        },
        {
          id: 'kinojo-nishimon',
          x: 0.74,
          y: 0.36,
          title: '復元された西門からのパノラマ',
          body: '現在は史跡調査や整備、復元が行われており、角楼跡や城門跡を訪れることができます。復元された西門から望むパノラマ風景は素晴らしく、城壁に沿って全長2.8キロのウォーキングコースも整備されています。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10755.html',
          mapQuery: '鬼ノ城 西門',
        },
        {
          id: 'kinojo-ura',
          x: 0.22,
          y: 0.5,
          title: '桃太郎の元になった温羅伝説',
          body: '桃太郎伝説のルーツとされる温羅退治の伝説では、鬼とされた温羅が総社市の鬼ノ城に住んでいたと語られます。岡山は「桃太郎伝説の生まれたまち」として2018年に日本遺産に認定されました。',
          source: 'https://www.okayama-kanko.jp/feature/momotarou',
          mapQuery: '総社市 鬼城山',
        },
      ],
    },
    {
      id: 'fukiya',
      name: '吹屋ふるさと村',
      area: '高梁市成羽町・昼過ぎ',
      image: '/assets/scenery/okayama/scenes/05_fukiya.webp',
      hotspots: [
        {
          id: 'fukiya-bengara',
          x: 0.46,
          y: 0.44,
          title: '赤で統一された鉱山町',
          body: '吹屋の町並みは、赤褐色の石州瓦とベンガラ塗りの格子で統一されています。ベンガラ生産とその原料であるローハで富を築いた豪商たちがつくり上げた町で、屋敷には精巧な意匠が施されています。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10876.html',
          mapQuery: '吹屋ふるさと村',
        },
        {
          id: 'fukiya-judenken',
          x: 0.74,
          y: 0.36,
          title: '県内でいちばん早く選ばれた保存地区',
          body: '高梁市吹屋は、文化庁の重要伝統的建造物群保存地区一覧に鉱山町として載っており、選定年月日は昭和52年5月18日です。同じ一覧にある岡山県内の5地区のなかで、もっとも早い選定にあたります。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '高梁市 吹屋',
        },
        {
          id: 'fukiya-sasaune',
          x: 0.22,
          y: 0.5,
          title: 'ベンガラで染める体験',
          body: '町内には旧吹屋小学校や銅山跡の笹畝坑道などの施設が点在し、繁栄の歴史をたどることができます。ベンガラの絵の具を使ってハンカチやバッグをつくる体験も用意されています。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10876.html',
          mapQuery: '笹畝坑道',
        },
      ],
    },
    {
      id: 'makido',
      name: '満奇洞',
      area: '新見市・午後',
      image: '/assets/scenery/okayama/scenes/06_makido.webp',
      hotspots: [
        {
          id: 'makido-namae',
          x: 0.46,
          y: 0.44,
          title: '歌人が名づけた鍾乳洞',
          body: '1929年に歌人の与謝野鉄幹・晶子夫婦が「奇に満ちた洞」と詠んだことから、満奇洞と呼ばれるようになりました。所在地は岡山県新見市豊永赤馬です。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10993.html',
          mapQuery: '満奇洞',
        },
        {
          id: 'makido-450m',
          x: 0.74,
          y: 0.34,
          title: '全長450メートル、県の天然記念物',
          body: '全長およそ450メートルの鍾乳洞で、県の天然記念物に指定されています。最奥の洞内湖にかかる竜宮橋や、日本屈指のリムストーンが広がる千枚田が見どころとして挙げられています。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10993.html',
          mapQuery: '満奇洞 竜宮橋',
        },
        {
          id: 'makido-15do',
          x: 0.22,
          y: 0.5,
          title: '洞内は一年中15度前後',
          body: '洞内は常に15度前後に保たれており、夏は涼しく冬は暖かいため通年で楽しめます。静寂に包まれた洞内はライトアップされており、神秘的な雰囲気がつくられています。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10993.html',
          mapQuery: '新見市 満奇洞',
        },
      ],
    },
    {
      id: 'shizutani-gakko',
      name: '旧閑谷学校',
      area: '備前市・晩秋の午後',
      image: '/assets/scenery/okayama/scenes/07_shizutani_gakko.webp',
      hotspots: [
        {
          id: 'shizutani-kokuho',
          x: 0.46,
          y: 0.42,
          title: '国宝の講堂がある学校跡',
          body: '藩主の池田光政が庶民教育の意をもち、寛文11年に津田永忠に命じて造営したものです。中心に国宝に指定されている講堂があり、ほとんどの建造物が国指定重要文化財となっています。特別史跡でもあります。',
          source: 'https://www.city.bizen.okayama.jp/site/bizen/655.html',
          mapQuery: '旧閑谷学校',
        },
        {
          id: 'shizutani-bizenyaki',
          x: 0.74,
          y: 0.36,
          title: '備前焼の瓦で葺いた屋根',
          body: '創建は寛文10年で、元禄14年に備前焼瓦葺きの講堂、習芸斎、飲室が成り、現在目にする閑谷学校の全容が整いました。屋根瓦が備前焼であることが、この建物の大きな特徴です。',
          source: 'https://shizutani.jp/japanese',
          mapQuery: '閑谷学校 講堂',
        },
        {
          id: 'shizutani-nihonisan',
          x: 0.22,
          y: 0.5,
          title: '最初の日本遺産に選ばれた',
          body: '平成27年4月、「近世日本の教育遺産群」として最初の日本遺産に認定されました。岡山藩主池田光政によって創建された、庶民のための公立学校です。',
          source: 'https://shizutani.jp/japanese',
          mapQuery: '特別史跡旧閑谷学校',
        },
      ],
    },
    {
      id: 'washuzan',
      name: '鷲羽山',
      area: '倉敷市下津井・日没',
      image: '/assets/scenery/okayama/scenes/08_washuzan.webp',
      hotspots: [
        {
          id: 'washuzan-namae',
          x: 0.46,
          y: 0.44,
          title: '鷲が羽を広げた形の山',
          body: '鷲羽山という名は、鷲が羽を広げた様子に似ていることから名付けられました。所在地は倉敷市下津井田之浦で、瀬戸内海屈指の多島美を眺めながら散策を楽しめます。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10427.html',
          mapQuery: '鷲羽山',
        },
        {
          id: 'washuzan-meisho',
          x: 0.74,
          y: 0.36,
          title: '国が名勝に指定した眺め',
          body: '鷲羽山展望台は瀬戸内海国立公園の代表的な景勝地で、国指定名勝にも指定されています。展望台は鷲羽山レストハウスと鷲羽山ビジターセンター付近に、あわせて2か所設けられています。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10430.html',
          mapQuery: '鷲羽山第一展望台',
        },
        {
          id: 'washuzan-yukei',
          x: 0.22,
          y: 0.5,
          title: '夕景で選ばれた多島美',
          body: '極上の夕景が望める場所として、日本の夕陽百選に選定されています。瀬戸内海屈指の多島美を眺めながらの散策も楽しめます。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10427.html',
          mapQuery: '鷲羽山 展望台',
        },
      ],
    },
    {
      id: 'okayama-jo',
      name: '岡山城',
      area: '岡山市北区・薄暮',
      image: '/assets/scenery/okayama/scenes/09_okayama_jo.webp',
      hotspots: [
        {
          id: 'okayamajo-ujo',
          x: 0.46,
          y: 0.38,
          title: '黒い下見板からきた烏城の名',
          body: '天守の外壁は黒塗りの下見板で覆われていて、烏城という別名があります。あわせて金箔瓦が出土しており、金烏城とも呼ばれてきました。',
          source: 'https://okayama-castle.jp/learn-history',
          mapQuery: '岡山城',
        },
        {
          id: 'okayamajo-gokakkei',
          x: 0.74,
          y: 0.52,
          title: '不等辺五角形をした天守台',
          body: '天守の石垣や1階の平面が不等辺五角形をしているのも特徴のひとつで、土台になった岡山の丘の地盤にあわせたためといわれています。慶長2年に天守ができたとされ、岡山城ではこれをもって築城年としています。',
          source: 'https://okayama-castle.jp/learn-history',
          mapQuery: '岡山城 天守',
        },
        {
          id: 'okayamajo-saiken',
          x: 0.22,
          y: 0.48,
          title: '空襲での焼失と令和の大改修',
          body: '築城当時の天守は1945年の空襲で焼失しましたが、現在は再建されています。2022年11月3日には令和の大改修を終えて、リニューアルオープンしました。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10002.html',
          mapQuery: '岡山城 烏城灯源郷',
        },
      ],
    },
    {
      id: 'kurashiki-bikan',
      name: '倉敷美観地区',
      area: '倉敷市・夜',
      image: '/assets/scenery/okayama/scenes/10_kurashiki_bikan.webp',
      hotspots: [
        {
          id: 'kurashiki-judenken',
          x: 0.46,
          y: 0.42,
          title: '商家町として国が選定',
          body: '倉敷市倉敷川畔は、文化庁の重要伝統的建造物群保存地区一覧に商家町として載っており、選定年月日は昭和54年5月21日です。岡山県内では高梁市吹屋に次いで2番目に早い選定にあたります。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '倉敷美観地区',
        },
        {
          id: 'kurashiki-namakokabe',
          x: 0.74,
          y: 0.36,
          title: '白壁・なまこ壁・柳並木',
          body: '白壁の蔵屋敷、なまこ壁、柳並木など、趣ある景観が続く地区です。伝統的な建物がつくる町並みと、倉敷川沿いのレトロモダンな風景が広がります。JR倉敷駅から徒歩およそ15分の距離にあります。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10226.html',
          mapQuery: '倉敷川 今橋',
        },
        {
          id: 'kurashiki-shomei',
          x: 0.22,
          y: 0.5,
          title: '夜は照明で別の顔になる',
          body: '照明デザイナーの石井幹子氏がプロデュースした倉敷美観地区夜間景観照明により、日中とは異なる景色が楽しめます。点灯の時間は季節によって変わるので、出かける前に最新の発表を確かめてください。',
          source: 'https://www.okayama-kanko.jp/spot/detail_10226.html',
          mapQuery: '倉敷美観地区 夜間景観照明',
        },
      ],
    },
  ],
};
